import { Suspense, useCallback, useEffect, useMemo, useState } from "react";

import { useParams } from "react-router-dom";
import { DashboardType } from "@doitintl/cmp-models";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, Stack } from "@mui/material";
import Grid from "@mui/material/Grid2";

import { useApiContext } from "../../../api/context";
import { dashboardsTxt } from "../../../assets/texts/Customer/dashboards";
import { AvaOpeningSource, useAvaDialogContext } from "../../../Components/Ava2/AvaDialogContextProvider";
import ProcessingState from "../../../Components/Dashboard/Common/ProcessingState/ProcessingState";
import {
  CreateDashboardDialog,
  type DashboardOperationType,
} from "../../../Components/Dashboard/CreateDashboardDialog";
import { DashboardGuard } from "../../../Components/Dashboard/DashboardGuard";
import { useDashboardNavigation } from "../../../Components/Dashboard/DashboardNavigartionContext";
import { Loader } from "../../../Components/Dashboard/Loader";
import {
  areAllWidgetsEmpty,
  excludeWidgetsByName,
  filterWidgetsByName,
  getDashboardType,
  isDashboardLoading,
} from "../../../Components/Dashboard/utils/widgetUtils";
import { WidgetsList } from "../../../Components/Dashboard/WidgetsList";
import DeleteDialog from "../../../Components/DeleteDialog";
import { DoitConsoleTitle } from "../../../Components/DoitConsoleTitle";
import { useCloudAnalyticsTransforms } from "../../../Components/hooks/cloudAnalytics/useCloudAnalyticsTransforms";
import { useDashboardsMetadata } from "../../../Components/hooks/cloudAnalytics/useDashboardsMetadata";
import { useSessionStorage } from "../../../Components/hooks/storageHooks";
import NoEntitlement from "../../../Components/NoEntitlement/NoEntitlement";
import { useInfoSnackbar, useSnackbar } from "../../../Components/SharedSnackbar/SharedSnackbar.context";
import { ThreeDotsMenu, type ThreeDotsMenuOption } from "../../../Components/ThreeDotsMenu";
import { isProduction } from "../../../constants";
import { useAuthContext } from "../../../Context/AuthContext";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { useDashboardProcessingContext } from "../../../Context/DashboardProcessingContext";
import { useIsFeatureEntitled, useTier } from "../../../Context/TierProvider";
import { useCurrentDashboardContext, type Widget } from "../../../Context/useCurrentDashboardContext";
import { isCustomerInPresentationMode } from "../../../Context/useCustomerOrPresentationModeCustomer";
import { useDashboardsContext } from "../../../Context/useDashboardsContext";
import { useUserContext } from "../../../Context/UserContext";
import PhoneDialog from "../../../Support/Components/PhoneDialog";
import { getUserOrgID } from "../../../utils/common";
import { useFullScreen } from "../../../utils/dialog";
import mixpanel from "../../../utils/mixpanel";
import { getWidgetId } from "../../../utils/widgets";
import { useFormatLink } from "../../CloudAnalytics/analyticsResources/hooks";
import { useNewReportHandler } from "../../CloudAnalytics/report/hooks";
import { updateVisibleDashboards } from "../db";
import DataImportBanner from "../SaaSConsoleOnboarding/DataImportBanner";
import DashboardEmptyState from "./DashboardEmptyState";
import { DashboardHeader } from "./DashboardHeader";
import { DashboardLayout } from "./DashboardLayout";
import { DashboardSideMenu } from "./DashboardSideMenu";
import { AddReportDialog } from "./Dialogs/AddReport/AddReportDialog";
import EmbeddedDashboard from "./EmbeddedDashboard";
import { EmptyDashboardWithActions } from "./States/EmptyDashboardWithActions";
import { EmptyDashboardWithMessage } from "./States/EmptyDashboardWithMessage";
import { type DashboardOperations } from "./useDashboardTypes";
import { sortDashboardsByType } from "./utils";
import { WidgetsGridPageWrapper } from "./WidgetsGrid/WidgetsGridPageWrapper";

const BQLensDashboardName = "BQ Lens";

export const CustomerDashboards = () => {
  const api = useApiContext();
  const { partnerCompany, isDoitPartner, isDoitEmployee } = useAuthContext({ mustHaveUser: true });
  const isDoitPartnerOrDoitEmployee = (isDoitPartner && !!partnerCompany) || isDoitEmployee;
  const {
    entities,
    entitiesLoading,
    customer: originalCustomer,
    customerOrPresentationModeCustomer: customer,
    userOrganization,
    isProductOnlyCustomer,
  } = useCustomerContext();
  const { getFeatureName } = useTier();
  const { userRoles } = useUserContext({ requiredRoles: true });
  const { isMobile } = useFullScreen();

  const isEntitledDashboardCustom = useIsFeatureEntitled("dashboard:custom");
  const isEntitledAvaIcon = useIsFeatureEntitled("platform:ava:icon");

  const [openOrganizationsDialog, setOpenOrganizationsDialog] = useState(false);

  // dashboard hooks
  const {
    dashboards,
    defaultDashboardName,
    setDefaultDashboard,
    deleteDashboardByName,
    detachDashboardByName,
    isLoading: dashboardsLoading,
  } = useDashboardsContext();
  const historyGotoDashboard = useDashboardNavigation();
  const { setCurrentDashboardByName, currentDashboard, isCustomDashboard, isEmbeddedDashboard } =
    useCurrentDashboardContext();

  const widgets = useMemo(() => currentDashboard?.widgets ?? [], [currentDashboard?.widgets]);

  // load transforms to populate the cache early as possible
  useCloudAnalyticsTransforms();

  const currentDashboardId = currentDashboard?.id ?? null;

  const { dashboardWidgetStates, setRefreshTriggerTime } = useDashboardProcessingContext();
  const { handleDashboardMetadataUpdate, checkRefreshNeeded } = useDashboardsMetadata(
    customer.id,
    getUserOrgID(isDoitEmployee, userOrganization),
    currentDashboardId
  );
  const [currentDashboardOperations, setCurrentDashboardOperations] = useState<DashboardOperations>({});

  // inner state
  const [widgetLibraryOpen, setWidgetLibraryOpen] = useState(false);
  const [dashboardOperationType, setDashboardOperationType] = useState<DashboardOperationType | undefined>();
  const { name } = useParams<{ name: string }>();
  const [lastOpenedDashboard, setLastOpenedDashboard] = useSessionStorage<string | undefined>(
    `last_opened_dashboard`,
    undefined
  );
  const [isPhoneOpen, setIsPhoneOpen] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddReportDialog, setOpenAddReportDialog] = useState(false);

  // components
  const sharedSnackbar = useSnackbar();
  const showInfoSnackbar = useInfoSnackbar();

  const { handleOpenAvaDialog } = useAvaDialogContext();
  const reportLink = useFormatLink({ type: "report" });
  const handleNewReport = useNewReportHandler({
    baseUrl: reportLink,
    mixpanelEventName: "analytics.reports.new",
    additionalState: {
      dashboardId: currentDashboard?.id,
      dashboardName: currentDashboard?.name,
    },
  });

  const emptyDashboard = !isEmbeddedDashboard && widgets.length === 0;

  const notAllowedToAddReport = !isEntitledDashboardCustom || (!userRoles.cloudAnalyticsUser && !isDoitEmployee);
  const reachedMaxWidgets = Boolean(currentDashboard?.reachedMaxWidgets);

  const hasAccessToAva = isDoitEmployee || !!isEntitledAvaIcon;

  const updateUserDefaultDashboard = useCallback(async () => {
    if (!currentDashboard) {
      return;
    }

    mixpanel.track("dashboards.set-as-default");
    await setDefaultDashboard(currentDashboard.id);
    sharedSnackbar.onOpen({
      message: `'${currentDashboard.name}' is set as your default dashboard`,
      variant: "success",
      autoHideDuration: 4000,
      action: [
        <IconButton key="close" aria-label="Close" color="inherit" onClick={sharedSnackbar.onClose} size="large">
          <CloseIcon />
        </IconButton>,
      ],
    });
  }, [currentDashboard, setDefaultDashboard, sharedSnackbar]);

  const isPresetDashboard = Boolean(currentDashboard?.dashboardType);

  /**
   * Retrieves the list of widgets to be rendered based on the current dashboard and cloud report states.
   */
  const [widgetsToRender, noCloudReportsData] = useMemo((): [Widget[], boolean] => {
    if (!currentDashboard || !isPresetDashboard) {
      return [widgets, false];
    }
    const dashboardType = getDashboardType(name);

    // Filter widgets related to cloud reports
    const cloudReportWidgets = filterWidgetsByName(widgets, "cloudReports");
    const widgetStates = dashboardWidgetStates[currentDashboard.name] || {};

    // Check if all cloud reports are empty
    const areCloudReportsEmpty = areAllWidgetsEmpty(widgetStates);

    // Remove fixed widgets if no cloud report is available
    if (widgets.length > 0 && areCloudReportsEmpty) {
      if (dashboardType === DashboardType.AwsLens || dashboardType === DashboardType.SaaSAwsLens) {
        return [excludeWidgetsByName(widgets, "flexSaveAws"), areCloudReportsEmpty];
      }
    }

    // Continue using the original widget array until all cloud reports are processed
    if (widgets.length > 0 && Object.keys(widgetStates).length !== cloudReportWidgets.length) {
      return [widgets, areCloudReportsEmpty];
    }

    // Filter cloud report widgets based on the availability of data
    const filteredWidgets = widgets.filter((item) => {
      if (item.name.includes("cloudReports")) {
        const widgetId = getWidgetId(item);

        return widgetStates[widgetId]?.hasData && item?.state !== "failed";
      }
      return true;
    });

    return [filteredWidgets, areCloudReportsEmpty];
  }, [currentDashboard, dashboardWidgetStates, name, widgets, isPresetDashboard]);

  const changeDashboardByNameAndUI = useCallback(
    (name: string) => {
      setLastOpenedDashboard(name);
      historyGotoDashboard(name);
    },
    [historyGotoDashboard, setLastOpenedDashboard]
  );

  const navigateToDefaultOrFirst = useCallback(() => {
    if (defaultDashboardName && currentDashboard?.name !== defaultDashboardName) {
      changeDashboardByNameAndUI(defaultDashboardName);
      return;
    }

    const dashboardsToSort = dashboards.filter((d) => d.name !== currentDashboard?.name);
    if (dashboardsToSort.length > 0) {
      const sorted = sortDashboardsByType(dashboardsToSort);
      changeDashboardByNameAndUI(sorted[0].name);
    } else {
      setLastOpenedDashboard("");
      setCurrentDashboardByName(undefined);
      historyGotoDashboard("");
    }
  }, [
    defaultDashboardName,
    currentDashboard?.name,
    dashboards,
    changeDashboardByNameAndUI,
    setLastOpenedDashboard,
    setCurrentDashboardByName,
    historyGotoDashboard,
  ]);

  const handleDeleteDashboard = useCallback(async () => {
    if (!currentDashboard) {
      return;
    }
    await deleteDashboardByName(currentDashboard.id);
    setOpenDeleteDialog(false);
  }, [currentDashboard, deleteDashboardByName]);

  const handleCreateReportUsingAva = useCallback(() => {
    handleOpenAvaDialog(AvaOpeningSource.DASHBOARDS, currentDashboard?.id);
  }, [currentDashboard?.id, handleOpenAvaDialog]);

  useEffect(() => {
    if (dashboardsLoading) {
      return;
    }
    if (!name) {
      if (lastOpenedDashboard && dashboards.some((d) => d.name === lastOpenedDashboard)) {
        changeDashboardByNameAndUI(lastOpenedDashboard);
      } else {
        navigateToDefaultOrFirst();
      }
    } else {
      setCurrentDashboardByName(name);
    }
  }, [
    name,
    dashboards,
    dashboardsLoading,
    setCurrentDashboardByName,
    changeDashboardByNameAndUI,
    defaultDashboardName,
    lastOpenedDashboard,
    navigateToDefaultOrFirst,
  ]);

  useEffect(() => {
    if (dashboardsLoading) {
      return;
    }

    // check if current dashboard is not in the array list anymore then navigate to a new one
    if (currentDashboard?.name && !dashboards.some((d) => d.name === currentDashboard?.name)) {
      navigateToDefaultOrFirst();
    }
  }, [currentDashboard, dashboards, navigateToDefaultOrFirst, dashboardsLoading]);

  const optionsForDashboard = useMemo(() => {
    const isAccountDashboard = currentDashboard?.name === "Account";
    const options: ThreeDotsMenuOption[] = [
      {
        key: "edit-dashboard-settings",
        label: "Edit dashboard settings",
        action: () => {
          mixpanel.track("dashboards.settings");
          setDashboardOperationType("edit");
        },
        disabled: !currentDashboardOperations.allowToEdit || isAccountDashboard,
      },
      {
        key: "set-default-dashboard",
        label: "Set as default",
        action: updateUserDefaultDashboard,
        disabled: defaultDashboardName === currentDashboard?.name,
      },
    ];

    options.push({
      key: "refresh-all",
      label: "Refresh all widgets",
      disabled: isEmbeddedDashboard,
      action: async () => {
        setRefreshTriggerTime();
      },
    });

    if (!isAccountDashboard && currentDashboardOperations.allowToDelete) {
      options.push({
        key: "delete-dashboard",
        label: "Delete dashboard",
        action: () => {
          setOpenDeleteDialog(true);
        },
        color: "error.main",
      });
    }

    if (!isAccountDashboard && currentDashboardOperations.allowToRemove && currentDashboard?.id) {
      options.push({
        key: "remove-dashboard",
        label: "Remove dashboard",
        action: async () => {
          await detachDashboardByName(currentDashboard.id);
        },
      });
    }

    return options;
  }, [
    currentDashboard?.name,
    currentDashboard?.id,
    currentDashboardOperations.allowToEdit,
    currentDashboardOperations.allowToDelete,
    currentDashboardOperations.allowToRemove,
    updateUserDefaultDashboard,
    defaultDashboardName,
    isEmbeddedDashboard,
    setRefreshTriggerTime,
    detachDashboardByName,
  ]);

  const processedWidgets = useMemo(
    () => (!currentDashboard ? {} : (dashboardWidgetStates?.[currentDashboard.name] ?? {})),
    [currentDashboard, dashboardWidgetStates]
  );

  const showNoEntitlement = useMemo(
    () =>
      isCustomDashboard &&
      !isEntitledDashboardCustom &&
      !(isCustomerInPresentationMode(originalCustomer) && getDashboardType(name) === BQLensDashboardName),
    [isCustomDashboard, isEntitledDashboardCustom, name, originalCustomer]
  );

  const loading = isDashboardLoading(widgets, name, processedWidgets);

  const [updatedTimeLastAccessed, setUpdatedTimeLastAccessed] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    const updateMetadata = async () => {
      if (!currentDashboardId) {
        return;
      }

      if (currentDashboardId !== "" && !updatedTimeLastAccessed[currentDashboardId] && !loading) {
        const cloudReportsWidgetsWithData = filterWidgetsByName(widgetsToRender, "cloudReports").filter((item) => {
          const widgetId = getWidgetId(item);
          return processedWidgets[widgetId];
        });

        if (cloudReportsWidgetsWithData.length > 0) {
          if (isProduction && isDoitPartnerOrDoitEmployee && currentDashboard?.ref?.path) {
            const refreshNeeded = await checkRefreshNeeded();
            if (refreshNeeded) {
              updateVisibleDashboards(api, customer.id, [currentDashboard?.ref.path]);
              showInfoSnackbar(dashboardsTxt.DASHBOARDS_ARE_BEING_UPDATED);
            }
          }

          await handleDashboardMetadataUpdate();

          setUpdatedTimeLastAccessed((prevState) => ({
            ...prevState,
            [currentDashboardId]: true,
          }));
        }
      }
    };
    updateMetadata();
  }, [
    api,
    checkRefreshNeeded,
    currentDashboard?.ref.path,
    currentDashboardId,
    customer.id,
    handleDashboardMetadataUpdate,
    isDoitEmployee,
    isDoitPartnerOrDoitEmployee,
    loading,
    processedWidgets,
    showInfoSnackbar,
    updatedTimeLastAccessed,
    userOrganization,
    widgetsToRender,
  ]);

  if (dashboardsLoading) {
    return <Loader />;
  }

  if (
    !isCustomerInPresentationMode(originalCustomer) &&
    !isProductOnlyCustomer &&
    !entitiesLoading &&
    (entities.length === 0 || !entities.some((x) => x.active))
  ) {
    return <DashboardEmptyState />;
  }

  const splitButtonOptions = [
    {
      label: "Add existing report",
      action: () => {
        setOpenAddReportDialog(true);
      },
      disabled: notAllowedToAddReport,
    },
    {
      label: "Add widget",
      action: () => {
        setWidgetLibraryOpen(true);
      },
    },
    {
      label: "Create a new report",
      action: handleNewReport,
      disabled: notAllowedToAddReport,
    },
    ...(hasAccessToAva
      ? [
          {
            label: "Create a report using Ava",
            action: handleCreateReportUsingAva,
            disabled: notAllowedToAddReport,
          },
        ]
      : []),
  ].sort((a) => (a.disabled ? 1 : -1));

  // -2 restore the padding that the header will start from zero
  return (
    <Box
      sx={{
        mx: -2,
        mt: -1,
        mb: -10,
      }}
    >
      {currentDashboard && <DoitConsoleTitle pageName="Dashboard" pageLevel1={currentDashboard.name} />}
      <Stack spacing={1}>
        <DashboardLayout
          menu={
            <DashboardSideMenu
              openOrganizationsDialog={openOrganizationsDialog}
              onOrganizationsDialogChanged={setOpenOrganizationsDialog}
              selectedDashboardId={currentDashboardId}
              onNavigateToDashboard={changeDashboardByNameAndUI}
              disableNewDashboardCreation={!isEntitledDashboardCustom}
              onUpdateCurrentDashboardOperations={setCurrentDashboardOperations}
              onNewDashboardClicked={() => {
                setDashboardOperationType("create");
              }}
            />
          }
          operations={<ThreeDotsMenu closeAfterSelect options={optionsForDashboard} />}
        >
          <Box>
            {loading ? (
              <Grid
                sx={{
                  mb: 1,
                  px: 2,
                }}
                size={12}
              >
                <ProcessingState dashboardType={getDashboardType(name)} />
              </Grid>
            ) : (
              <Grid
                container
                spacing={1}
                columnSpacing={0}
                rowSpacing={0}
                sx={{
                  ml: "-0.5rem",
                  pl: {
                    xs: 2,
                    sm: 1,
                  },

                  pr: {
                    xs: 1,
                    sm: 0,
                  },
                }}
              >
                <Suspense>
                  <DashboardGuard>
                    <>
                      {!isMobile && currentDashboard && (
                        <DashboardHeader
                          threeDotsMenuOptions={optionsForDashboard}
                          splitButtonOptions={splitButtonOptions}
                          allowToAddWidgets={!!currentDashboardOperations.allowToAddWidgets}
                          disableButtonsWithTooltip={reachedMaxWidgets}
                        />
                      )}

                      <DataImportBanner dashboardType={getDashboardType(name)} />

                      {showNoEntitlement && (
                        <NoEntitlement
                          feature={getFeatureName("dashboard:custom") ?? "this feature"}
                          inPresentationMode={originalCustomer.presentationMode?.enabled}
                        />
                      )}

                      {currentDashboard ? (
                        <>
                          {!showNoEntitlement && emptyDashboard && (
                            <>
                              {currentDashboardOperations?.allowToAddWidgets ? (
                                <EmptyDashboardWithActions
                                  onAddExistingReportClicked={() => {
                                    setOpenAddReportDialog(true);
                                  }}
                                  onNewReportClicked={handleNewReport}
                                  onNewWidgetClicked={() => {
                                    setWidgetLibraryOpen(true);
                                  }}
                                  onAvaClicked={handleCreateReportUsingAva}
                                  showAva={hasAccessToAva}
                                />
                              ) : (
                                <EmptyDashboardWithMessage
                                  title={dashboardsTxt.NO_ALLOWED_TO_EDIT.title}
                                  subtitle={dashboardsTxt.NO_ALLOWED_TO_EDIT.subtitle}
                                />
                              )}
                            </>
                          )}

                          {!showNoEntitlement && !emptyDashboard && !isEmbeddedDashboard && (
                            <WidgetsGridPageWrapper
                              showSummaryCards={
                                currentDashboard.dashboardType === DashboardType.Pulse && !noCloudReportsData
                              }
                              disableAutoRefreshing={isPresetDashboard}
                              widgets={widgetsToRender}
                            />
                          )}

                          {isEmbeddedDashboard && <EmbeddedDashboard dashboardType={currentDashboard.dashboardType} />}
                        </>
                      ) : (
                        <EmptyDashboardWithMessage
                          title={dashboardsTxt.NO_DASHBOARDS.title}
                          subtitle={dashboardsTxt.NO_DASHBOARDS.subtitle}
                          dataCy="no-dashboards"
                        >
                          <Stack direction="row" spacing={3}>
                            <Button
                              variant="contained"
                              data-cy="select-dashboard"
                              onClick={() => {
                                setOpenOrganizationsDialog(true);
                              }}
                            >
                              {dashboardsTxt.SELECT_DASHBOARD}
                            </Button>
                            <Button
                              variant="text"
                              data-cy="create-new-dashboard"
                              onClick={() => {
                                setDashboardOperationType("create");
                              }}
                            >
                              {dashboardsTxt.CREATE_NEW}
                            </Button>
                          </Stack>
                        </EmptyDashboardWithMessage>
                      )}
                    </>
                  </DashboardGuard>
                </Suspense>
              </Grid>
            )}
          </Box>
        </DashboardLayout>
      </Stack>
      {!isDoitPartner && (
        <>
          {isPhoneOpen && (
            <PhoneDialog
              onClose={() => {
                setIsPhoneOpen(false);
              }}
            />
          )}
        </>
      )}
      {dashboardOperationType && (
        <CreateDashboardDialog
          type={dashboardOperationType}
          selectedDashboard={currentDashboard ?? null}
          handleClose={(name) => {
            setDashboardOperationType(undefined);
            if (name) {
              historyGotoDashboard(name);
            }
          }}
        />
      )}
      <DeleteDialog
        open={openDeleteDialog}
        title="Delete dashboard?"
        message="Are you sure you want to delete the dashboard?"
        onDelete={handleDeleteDashboard}
        onClose={() => {
          setOpenDeleteDialog(false);
        }}
        deleteButtonText="Delete dashboard"
      />
      {!notAllowedToAddReport && openAddReportDialog && (
        <Suspense>
          <AddReportDialog
            open={openAddReportDialog}
            selectedWidgetNames={widgetsToRender.map((w) => w.name)}
            onClose={() => {
              setOpenAddReportDialog(false);
            }}
          />
        </Suspense>
      )}
      {widgetLibraryOpen && (
        <WidgetsList
          onClose={() => {
            setWidgetLibraryOpen(false);
          }}
        />
      )}
    </Box>
  );
};
